<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-dialog" @click.stop>
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ modalTitle }}</h3>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="drill-down-summary">
            <div class="summary-card">
              <h4>{{ drillDownData.injuryType || "All Types" }}</h4>
              <p class="summary-count">{{ drillDownData.count }} injuries</p>
              <p class="summary-context">{{ contextText }}</p>
            </div>
          </div>

          <div class="drill-down-details">
            <h4>Detailed Breakdown</h4>
            <div class="details-grid">
              <div class="detail-item">
                <span class="label">Type:</span>
                <span class="value">{{
                  drillDownData.injuryType || "Various"
                  }}</span>
              </div>
              <div class="detail-item" v-if="drillDownData.week">
                <span class="label">Week:</span>
                <span class="value">{{ formatWeek(drillDownData.week) }}</span>
              </div>
              <div class="detail-item" v-if="drillDownData.period">
                <span class="label">Period:</span>
                <span class="value">{{
                  formatPeriod(drillDownData.period)
                  }}</span>
              </div>
              <div class="detail-item" v-if="drillDownData.teamName">
                <span class="label">Team:</span>
                <span class="value">{{ drillDownData.teamName }}</span>
              </div>
            </div>
          </div>

          <div class="drill-down-actions">
            <button class="btn btn-primary" @click="viewFullReport">
              View Full Report
            </button>
            <button class="btn btn-secondary" @click="exportData">
              Export Data
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "InjuryDrillDownModal",
  props: {
    drillDownData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    modalTitle() {
      switch (this.drillDownData.type) {
        case "trend":
          return "Injury Trend Details";
        case "distribution":
          return "Injury Distribution Details";
        case "heatmap":
          return "Heat Map Details";
        default:
          return "Injury Details";
      }
    },
    contextText() {
      switch (this.drillDownData.type) {
        case "trend":
          return `Injuries during the week of ${this.formatWeek(this.drillDownData.week)}`;
        case "distribution":
          return `Total injuries of this type across all teams and periods`;
        case "heatmap":
          return `Injuries for ${this.drillDownData.teamName} during ${this.formatPeriod(this.drillDownData.period)}`;
        default:
          return "Injury data details";
      }
    },
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },

    formatWeek(weekString) {
      const date = new Date(weekString);
      return date.toLocaleDateString("en-GB", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },

    formatPeriod(period) {
      const [year, month] = period.split("-");
      const date = new Date(year, month - 1);
      return date.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      });
    },

    viewFullReport() {
      // This could emit an event to navigate to a detailed report
      this.$emit("view-full-report", this.drillDownData);
      this.closeModal();
    },

    exportData() {
      // This could emit an event to export the filtered data
      this.$emit("export-data", this.drillDownData);
      this.closeModal();
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-dialog {
  width: 90%;
  max-width: 600px;
  max-height: 100vh;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  height: 100vh;
  overflow: scroll;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
  margin: 0;
  color: #283593;
  font-size: 1.5rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #dc3545;
}

.modal-body {
  padding: 20px;
}

.drill-down-summary {
  margin-bottom: 20px;
}

.summary-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.summary-card h4 {
  margin: 0 0 10px 0;
  color: #283593;
  font-size: 1.3rem;
}

.summary-count {
  font-size: 2rem;
  font-weight: bold;
  color: #dc3545;
  margin: 10px 0;
}

.summary-context {
  color: #6c757d;
  margin: 10px 0 0 0;
  font-size: 0.9rem;
}

.drill-down-details {
  margin-bottom: 20px;
}

.drill-down-details h4 {
  margin: 0 0 15px 0;
  color: #283593;
  font-size: 1.1rem;
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
}

.label {
  font-weight: 600;
  color: #495057;
}

.value {
  color: #283593;
  font-weight: 500;
}

.drill-down-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}

@media (max-width: 768px) {
  .modal-dialog {
    width: 95%;
  }

  .modal-header,
  .modal-body {
    padding: 15px;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .drill-down-actions {
    flex-direction: column;
  }
}
</style>
